<script lang="ts">
export default {
  name: 'NotificationPreferences',
};
</script>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, reactive, ref } from 'vue';
import notificationService from '@/services/v2/NotificationService';
import { useUser } from '@/composables/useUser';
import { UserRole } from '@/types/UserRole';
import {
  RefreshCwIcon,
  FileTextIcon,
  FolderUpIcon,
  UploadCloudIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  BarChart3Icon,
  FileSearchIcon,
  ChevronDownIcon,
  UserIcon,
  StarIcon,
  LoaderIcon,
  CheckIcon,
  BellIcon,
  MailIcon,
} from '@lucide/vue';

// -----
// refs
// -----

const loading = ref(false);
const saveStatus = ref<'idle' | 'saving' | 'saved' | 'timestamp'>('idle');
const savedTimestamp = ref('');
let saveStatusTimer: ReturnType<typeof setTimeout> | undefined;
let debounceTimer: ReturnType<typeof setTimeout> | undefined;
const generalSettingsOpen = ref(false);
const { role } = useUser();

const isClientUser = computed(() => {
  return role.value === UserRole.Client;
});

const isAdminUser = computed(() => {
  return role.value === UserRole.Admin;
});

// Brokers and clients start opted out; other roles keep the opted-in defaults declared below.
const usesOptOutDefaults = computed(() => {
  return role.value === UserRole.Broker || role.value === UserRole.Client;
});

const generalPrefs = reactive({
  notificationsEnabled: true,
  sendEmail: true,
});

const renewalPrefs = reactive({
  enabled: true,
  sendEmail: true,
  servicingRole: true,
  favorited: true,
  daysBefore30: false,
  daysBefore60: false,
  daysBefore90: false,
  daysBefore120: true,
});

const newPolicyPrefs = reactive({
  enabled: true,
  sendEmail: true,
  servicingRole: true,
  favorited: true,
});

const newDocumentUploadPrefs = reactive({
  enabled: false,
  sendEmail: true,
  servicingRole: true,
  favorited: true,
});

const newClientDocumentUploadPrefs = reactive({
  enabled: false,
  sendEmail: true,
  servicingRole: true,
  favorited: true,
});

const binderReadyForValidationPrefs = reactive({
  enabled: false,
  sendEmail: true,
  servicingRole: true,
  favorited: true,
});

const newAnalyticsResourcePrefs = reactive({
  enabled: true,
  sendEmail: true,
});

const documentExceptionPrefs = reactive({
  enabled: false,
  sendEmail: true,
  noMapping: true,
  failed: true,
  exception: true,
});

// In-app defaults on for every role so this group is deliberately left out of
// allPrefGroups below — the opt-out sweep there would blank it for brokers.
const externalPolicyReadyForReviewPrefs = reactive({
  enabled: true,
  sendEmail: false,
});

const renewalDaysConfig = [
  { key: 'daysBefore30' as keyof typeof renewalPrefs, label: '30' },
  { key: 'daysBefore60' as keyof typeof renewalPrefs, label: '60' },
  { key: 'daysBefore90' as keyof typeof renewalPrefs, label: '90' },
  { key: 'daysBefore120' as keyof typeof renewalPrefs, label: '120' },
];

const docExceptionStatusConfig = [
  { key: 'noMapping' as keyof typeof documentExceptionPrefs, label: 'No Mapping' },
  { key: 'failed' as keyof typeof documentExceptionPrefs, label: 'Failed' },
  { key: 'exception' as keyof typeof documentExceptionPrefs, label: 'Exception' },
];

const allPrefGroups = [
  generalPrefs,
  renewalPrefs,
  newPolicyPrefs,
  newDocumentUploadPrefs,
  newClientDocumentUploadPrefs,
  binderReadyForValidationPrefs,
  newAnalyticsResourcePrefs,
  documentExceptionPrefs,
];

// Derived from the group's own keys so a new preference can't be missed here later.
function disableAll(prefs: Record<string, boolean>): void {
  for (const key of Object.keys(prefs)) {
    prefs[key] = false;
  }
}

// ----------------
// lifecycle hooks
// ----------------

onBeforeMount(async () => {
  loading.value = true;
  const prefs = await notificationService.getPreferences();

  if (!prefs) {
    if (usesOptOutDefaults.value) {
      allPrefGroups.forEach(disableAll);
    }
    loading.value = false;
    return;
  }

  generalPrefs.notificationsEnabled = prefs.enabled;
  generalPrefs.sendEmail = prefs.sendEmail;

  renewalPrefs.enabled = prefs.renewalEnabled;
  renewalPrefs.sendEmail = prefs.renewalSendEmail;
  renewalPrefs.servicingRole = prefs.renewalServicingRole;
  renewalPrefs.favorited = prefs.renewalFavorited;
  renewalPrefs.daysBefore30 = prefs.renewal30Days;
  renewalPrefs.daysBefore60 = prefs.renewal60Days;
  renewalPrefs.daysBefore90 = prefs.renewal90Days;
  renewalPrefs.daysBefore120 = prefs.renewal120Days;

  newPolicyPrefs.enabled = prefs.newPolicyEnabled;
  newPolicyPrefs.sendEmail = prefs.newPolicySendEmail;
  newPolicyPrefs.servicingRole = prefs.newPolicyServicingRole;
  newPolicyPrefs.favorited = prefs.newPolicyFavorited;

  newDocumentUploadPrefs.enabled = prefs.newDocumentUploadEnabled;
  newDocumentUploadPrefs.sendEmail = prefs.newDocumentUploadSendEmail;
  newDocumentUploadPrefs.servicingRole = prefs.newDocumentUploadServicingRole;
  newDocumentUploadPrefs.favorited = prefs.newDocumentUploadFavorited;

  newClientDocumentUploadPrefs.enabled = prefs.newClientDocumentUploadEnabled;
  newClientDocumentUploadPrefs.sendEmail = prefs.newClientDocumentUploadSendEmail;
  newClientDocumentUploadPrefs.servicingRole = prefs.newClientDocumentUploadServicingRole;
  newClientDocumentUploadPrefs.favorited = prefs.newClientDocumentUploadFavorited;

  binderReadyForValidationPrefs.enabled = prefs.binderReadyForValidationEnabled;
  binderReadyForValidationPrefs.sendEmail = prefs.binderReadyForValidationSendEmail;
  binderReadyForValidationPrefs.servicingRole = prefs.binderReadyForValidationServicingRole;
  binderReadyForValidationPrefs.favorited = prefs.binderReadyForValidationFavorited;

  newAnalyticsResourcePrefs.enabled = prefs.newAnalyticsResourcePublishedEnabled;
  newAnalyticsResourcePrefs.sendEmail = prefs.newAnalyticsResourcePublishedSendEmail;

  documentExceptionPrefs.enabled = prefs.documentExceptionEnabled;
  documentExceptionPrefs.sendEmail = prefs.documentExceptionSendEmail;
  documentExceptionPrefs.noMapping = prefs.documentExceptionNoMapping ?? true;
  documentExceptionPrefs.failed = prefs.documentExceptionFailed ?? true;
  documentExceptionPrefs.exception = prefs.documentExceptionException ?? true;

  // Preferences saved before this type existed have no row for it, so fall back to the
  // same defaults the API applies rather than showing the user an unchecked box.
  externalPolicyReadyForReviewPrefs.enabled = prefs.externalPolicyReadyForReviewEnabled ?? true;
  externalPolicyReadyForReviewPrefs.sendEmail = prefs.externalPolicyReadyForReviewSendEmail ?? false;

  loading.value = false;
});

onBeforeUnmount(() => {
  clearTimeout(saveStatusTimer);
  clearTimeout(debounceTimer);
});

// ---------------------
// handlers / functions
// ---------------------

function handlePreferenceUpdate(): void {
  clearTimeout(debounceTimer);
  clearTimeout(saveStatusTimer);
  saveStatus.value = 'saving';
  debounceTimer = setTimeout(async () => {
    const savingStart = Date.now();
    await notificationService.updatePreferences({
      enabled: generalPrefs.notificationsEnabled,
      sendEmail: generalPrefs.sendEmail,
      renewalEnabled: renewalPrefs.enabled,
      renewalSendEmail: renewalPrefs.sendEmail,
      renewalServicingRole: renewalPrefs.servicingRole,
      renewalFavorited: renewalPrefs.favorited,
      renewal30Days: renewalPrefs.daysBefore30,
      renewal60Days: renewalPrefs.daysBefore60,
      renewal90Days: renewalPrefs.daysBefore90,
      renewal120Days: renewalPrefs.daysBefore120,
      newPolicyEnabled: newPolicyPrefs.enabled,
      newPolicySendEmail: newPolicyPrefs.sendEmail,
      newPolicyServicingRole: newPolicyPrefs.servicingRole,
      newPolicyFavorited: newPolicyPrefs.favorited,
      newDocumentUploadEnabled: newDocumentUploadPrefs.enabled,
      newDocumentUploadSendEmail: newDocumentUploadPrefs.sendEmail,
      newDocumentUploadServicingRole: newDocumentUploadPrefs.servicingRole,
      newDocumentUploadFavorited: newDocumentUploadPrefs.favorited,
      newClientDocumentUploadEnabled: newClientDocumentUploadPrefs.enabled,
      newClientDocumentUploadSendEmail: newClientDocumentUploadPrefs.sendEmail,
      newClientDocumentUploadServicingRole: newClientDocumentUploadPrefs.servicingRole,
      newClientDocumentUploadFavorited: newClientDocumentUploadPrefs.favorited,
      binderReadyForValidationEnabled: binderReadyForValidationPrefs.enabled,
      binderReadyForValidationSendEmail: binderReadyForValidationPrefs.sendEmail,
      binderReadyForValidationServicingRole: binderReadyForValidationPrefs.servicingRole,
      binderReadyForValidationFavorited: binderReadyForValidationPrefs.favorited,
      newAnalyticsResourcePublishedEnabled: newAnalyticsResourcePrefs.enabled,
      newAnalyticsResourcePublishedSendEmail: newAnalyticsResourcePrefs.sendEmail,
      documentExceptionEnabled: documentExceptionPrefs.enabled,
      documentExceptionSendEmail: documentExceptionPrefs.sendEmail,
      documentExceptionNoMapping: documentExceptionPrefs.noMapping,
      documentExceptionFailed: documentExceptionPrefs.failed,
      documentExceptionException: documentExceptionPrefs.exception,
      externalPolicyReadyForReviewEnabled: externalPolicyReadyForReviewPrefs.enabled,
      externalPolicyReadyForReviewSendEmail: externalPolicyReadyForReviewPrefs.sendEmail,
    });
    const elapsed = Date.now() - savingStart;
    const remaining = Math.max(0, 500 - elapsed);
    await new Promise((resolve) => setTimeout(resolve, remaining));
    savedTimestamp.value = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    saveStatus.value = 'saved';
    saveStatusTimer = setTimeout(() => {
      saveStatus.value = 'timestamp';
    }, 1000);
  }, 300);
}
</script>

<template>
  <div class="flex w-full flex-col items-center pt-5">
    <h1>Manage Your Notifications</h1>
    <div class="relative flex h-8 items-center justify-center">
      <Transition
        name="save-badge"
        mode="out-in">
        <span
          v-if="saveStatus === 'saving'"
          key="saving"
          class="absolute inline-flex items-center gap-1.5 rounded-full border border-solid border-gray-200 bg-white px-3 py-1 text-xs text-gray-500 shadow-sm">
          <LoaderIcon
            :size="12"
            class="animate-spin" />
          Saving...
        </span>
        <span
          v-else-if="saveStatus === 'saved'"
          key="saved"
          class="absolute inline-flex items-center gap-1.5 rounded-full border border-solid border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700 shadow-sm">
          <CheckIcon :size="12" />
          Saved
        </span>
        <span
          v-else-if="saveStatus === 'timestamp'"
          key="timestamp"
          class="absolute text-xs whitespace-nowrap text-gray-400">
          Saved - {{ savedTimestamp }}
        </span>
      </Transition>
    </div>

    <main
      v-show="!loading"
      class="mt-5 w-full max-w-4xl px-4 pb-10 sm:px-16">
      <!-- General Settings - Collapsible Status Strip -->
      <section class="mb-5 rounded-xl border border-solid border-gray-200 bg-white">
        <!-- Status strip (always visible) -->
        <div
          class="flex items-center justify-between px-5 py-3 select-none"
          :class="!generalSettingsOpen ? 'cursor-pointer' : ''"
          @click="!generalSettingsOpen && (generalSettingsOpen = true)">
          <div class="flex items-center gap-3 text-sm">
            <span
              class="size-2 rounded-full"
              :class="generalPrefs.notificationsEnabled ? 'bg-emerald-500' : 'bg-gray-300'"></span>
            <span class="font-semibold text-gray-900">General settings</span>
            <span class="text-gray-400">
              Notifications
              <span
                class="font-medium"
                :class="generalPrefs.notificationsEnabled ? 'text-gray-700' : 'text-gray-400'"
                >{{ generalPrefs.notificationsEnabled ? 'on' : 'off' }}</span
              >
              <span class="mx-1">&middot;</span>
              Email summary
              <span
                class="font-medium"
                :class="generalPrefs.sendEmail && generalPrefs.notificationsEnabled ? 'text-gray-700' : 'text-gray-400'"
                >{{ generalPrefs.sendEmail && generalPrefs.notificationsEnabled ? 'on' : 'off' }}</span
              >
            </span>
          </div>
          <button
            class="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            @click.stop="generalSettingsOpen = !generalSettingsOpen">
            {{ generalSettingsOpen ? 'Hide' : 'Edit' }}
            <ChevronDownIcon
              :size="14"
              class="transition-transform"
              :class="generalSettingsOpen ? 'rotate-180' : ''" />
          </button>
        </div>

        <!-- Expanded controls -->
        <div
          v-if="generalSettingsOpen"
          class="flex flex-col gap-4 border-t border-solid border-gray-200 px-5 py-4">
          <label
            for="notificationsEnabled"
            class="flex cursor-pointer items-center justify-between">
            <div>
              <div class="text-sm font-medium text-gray-900 select-none">Notifications enabled</div>
              <div class="text-xs text-gray-400">Enable or disable all email notifications</div>
            </div>
            <input
              type="checkbox"
              id="notificationsEnabled"
              @change="handlePreferenceUpdate"
              v-model="generalPrefs.notificationsEnabled"
              :checked="generalPrefs.notificationsEnabled"
              class="toggle border border-solid border-gray-400 toggle-md toggle-success" />
          </label>

          <label
            for="sendEmail"
            class="flex cursor-pointer items-center justify-between">
            <div>
              <div class="text-sm font-medium text-gray-900 select-none">Email notification summary</div>
              <div class="text-xs text-gray-400">Bundles checked Email rows into a digest</div>
            </div>
            <input
              type="checkbox"
              id="sendEmail"
              :disabled="!generalPrefs.notificationsEnabled"
              @change="handlePreferenceUpdate"
              v-model="generalPrefs.sendEmail"
              :checked="generalPrefs.sendEmail"
              class="toggle border border-solid border-gray-400 toggle-md toggle-success" />
          </label>
        </div>
      </section>

      <!-- Notification Matrix (non-client users) -->
      <section
        v-if="!isClientUser"
        class="overflow-hidden rounded-xl border border-solid border-gray-200 drop-shadow-xs">
        <table class="w-full">
          <thead>
            <tr class="border-b border-solid border-gray-200 bg-gray-50 text-xs tracking-wide text-gray-500 uppercase">
              <th class="px-5 py-3 text-left font-semibold">Notification</th>
              <th class="w-16 px-3 py-3 text-center font-semibold text-nowrap">In-App</th>
              <th class="w-16 px-3 py-3 text-center font-semibold">Email</th>
              <th class="w-16 px-3 py-3 text-center font-semibold">
                <span class="inline-flex items-center gap-1"
                  ><UserIcon
                    :size="12"
                    fill="currentColor" />Role</span
                >
              </th>
              <th class="w-16 px-3 py-3 text-center font-semibold">
                <span class="inline-flex items-center gap-1"
                  ><StarIcon
                    :size="12"
                    fill="currentColor" />Fav</span
                >
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <!-- Renewal -->
            <tr class="hover:bg-gray-50/50">
              <td class="px-5 py-4">
                <div class="flex items-start gap-3">
                  <RefreshCwIcon
                    :size="18"
                    class="mt-0.5 shrink-0"
                    :class="renewalPrefs.enabled ? 'text-emerald-500' : 'text-gray-400'" />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Renewal</div>
                    <div class="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
                      <span>Alert at</span>
                      <button
                        v-for="days in renewalDaysConfig"
                        :key="days.key"
                        type="button"
                        :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled"
                        @click="
                          renewalPrefs[days.key] = !renewalPrefs[days.key];
                          handlePreferenceUpdate();
                        "
                        class="rounded-full px-2 py-0.5 text-xs font-medium transition-colors"
                        :class="
                          renewalPrefs[days.key]
                            ? 'border border-solid border-emerald-300 bg-emerald-100 text-emerald-700'
                            : 'border border-solid border-gray-200 bg-gray-100 text-gray-400'
                        ">
                        {{ days.label }}
                      </button>
                      <span>days before expiration</span>
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="renewalEnabled"
                  :disabled="!generalPrefs.notificationsEnabled"
                  @change="handlePreferenceUpdate"
                  v-model="renewalPrefs.enabled"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="renewalEmail"
                  :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled || !generalPrefs.sendEmail"
                  @change="handlePreferenceUpdate"
                  v-model="renewalPrefs.sendEmail"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="renewalServicingRole"
                  :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="renewalPrefs.servicingRole"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="renewalFavorited"
                  :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="renewalPrefs.favorited"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
            </tr>

            <!-- New Policies in Epic -->
            <tr class="hover:bg-gray-50/50">
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <FileTextIcon
                    :size="18"
                    class="shrink-0"
                    :class="newPolicyPrefs.enabled ? 'text-emerald-500' : 'text-gray-400'" />
                  <div class="text-sm font-medium text-gray-900">New policies in Epic</div>
                </div>
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newPolicyEnabled"
                  :disabled="!generalPrefs.notificationsEnabled"
                  @change="handlePreferenceUpdate"
                  v-model="newPolicyPrefs.enabled"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newPolicySendEmail"
                  :disabled="!generalPrefs.notificationsEnabled || !newPolicyPrefs.enabled || !generalPrefs.sendEmail"
                  @change="handlePreferenceUpdate"
                  v-model="newPolicyPrefs.sendEmail"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newPolicyServicingRole"
                  :disabled="!generalPrefs.notificationsEnabled || !newPolicyPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="newPolicyPrefs.servicingRole"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newPolicyFavorited"
                  :disabled="!generalPrefs.notificationsEnabled || !newPolicyPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="newPolicyPrefs.favorited"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
            </tr>

            <!-- Account/Policy Uploaded Documents -->
            <tr class="hover:bg-gray-50/50">
              <td class="px-5 py-4">
                <div class="flex items-start gap-3">
                  <FolderUpIcon
                    :size="18"
                    class="mt-0.5 shrink-0"
                    :class="newDocumentUploadPrefs.enabled ? 'text-emerald-500' : 'text-gray-400'" />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Account / policy docs</div>
                    <div class="text-xs text-gray-400"></div>
                  </div>
                </div>
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newUploadDocumentEnabled"
                  :disabled="!generalPrefs.notificationsEnabled"
                  @change="handlePreferenceUpdate"
                  v-model="newDocumentUploadPrefs.enabled"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newUploadDocumentSendEmail"
                  :disabled="!generalPrefs.notificationsEnabled || !newDocumentUploadPrefs.enabled || !generalPrefs.sendEmail"
                  @change="handlePreferenceUpdate"
                  v-model="newDocumentUploadPrefs.sendEmail"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newUploadDocumentServicingRole"
                  :disabled="!generalPrefs.notificationsEnabled || !newDocumentUploadPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="newDocumentUploadPrefs.servicingRole"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newUploadDocumentFavorited"
                  :disabled="!generalPrefs.notificationsEnabled || !newDocumentUploadPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="newDocumentUploadPrefs.favorited"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
            </tr>

            <!-- Client Uploaded Documents -->
            <tr class="hover:bg-gray-50/50">
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <UploadCloudIcon
                    :size="18"
                    class="shrink-0"
                    :class="newClientDocumentUploadPrefs.enabled ? 'text-emerald-500' : 'text-gray-400'" />
                  <div class="text-sm font-medium text-gray-900">Client uploaded docs</div>
                </div>
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newClientDocumentUploadEnabled"
                  :disabled="!generalPrefs.notificationsEnabled"
                  @change="handlePreferenceUpdate"
                  v-model="newClientDocumentUploadPrefs.enabled"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newClientDocumentUploadSendEmail"
                  :disabled="!generalPrefs.notificationsEnabled || !newClientDocumentUploadPrefs.enabled || !generalPrefs.sendEmail"
                  @change="handlePreferenceUpdate"
                  v-model="newClientDocumentUploadPrefs.sendEmail"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newClientDocumentUploadServicingRole"
                  :disabled="!generalPrefs.notificationsEnabled || !newClientDocumentUploadPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="newClientDocumentUploadPrefs.servicingRole"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newClientDocumentUploadFavorited"
                  :disabled="!generalPrefs.notificationsEnabled || !newClientDocumentUploadPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="newClientDocumentUploadPrefs.favorited"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
            </tr>

            <!-- Binder Ready for Validation -->
            <tr class="hover:bg-gray-50/50">
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <CheckCircleIcon
                    :size="18"
                    class="shrink-0"
                    :class="binderReadyForValidationPrefs.enabled ? 'text-emerald-500' : 'text-gray-400'" />
                  <div class="text-sm font-medium text-gray-900">Binder ready for validation</div>
                </div>
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="binderReadyForValidationEnabled"
                  :disabled="!generalPrefs.notificationsEnabled"
                  @change="handlePreferenceUpdate"
                  v-model="binderReadyForValidationPrefs.enabled"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="binderReadyForValidationSendEmail"
                  :disabled="!generalPrefs.notificationsEnabled || !binderReadyForValidationPrefs.enabled || !generalPrefs.sendEmail"
                  @change="handlePreferenceUpdate"
                  v-model="binderReadyForValidationPrefs.sendEmail"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="binderReadyForValidationServicingRole"
                  :disabled="!generalPrefs.notificationsEnabled || !binderReadyForValidationPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="binderReadyForValidationPrefs.servicingRole"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="binderReadyForValidationFavorited"
                  :disabled="!generalPrefs.notificationsEnabled || !binderReadyForValidationPrefs.enabled"
                  @change="handlePreferenceUpdate"
                  v-model="binderReadyForValidationPrefs.favorited"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
            </tr>

            <!-- Document Exception (Admin only) -->
            <tr
              v-if="isAdminUser"
              class="hover:bg-gray-50/50">
              <td class="px-5 py-4">
                <div class="flex items-start gap-3">
                  <AlertTriangleIcon
                    :size="18"
                    class="mt-0.5 shrink-0"
                    :class="documentExceptionPrefs.enabled ? 'text-emerald-500' : 'text-gray-400'" />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Document exception</div>
                    <div class="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
                      <span>Statuses:</span>
                      <button
                        v-for="status in docExceptionStatusConfig"
                        :key="status.key"
                        type="button"
                        :disabled="!generalPrefs.notificationsEnabled || !documentExceptionPrefs.enabled"
                        @click="
                          documentExceptionPrefs[status.key] = !documentExceptionPrefs[status.key];
                          handlePreferenceUpdate();
                        "
                        class="rounded-full px-2 py-0.5 text-xs font-medium transition-colors"
                        :class="
                          documentExceptionPrefs[status.key]
                            ? 'border border-solid border-emerald-300 bg-emerald-100 text-emerald-700'
                            : 'border border-solid border-gray-200 bg-gray-100 text-gray-400'
                        ">
                        {{ status.label }}
                      </button>
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="documentExceptionEnabled"
                  :disabled="!generalPrefs.notificationsEnabled"
                  @change="handlePreferenceUpdate"
                  v-model="documentExceptionPrefs.enabled"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="documentExceptionSendEmail"
                  :disabled="!generalPrefs.notificationsEnabled || !documentExceptionPrefs.enabled || !generalPrefs.sendEmail"
                  @change="handlePreferenceUpdate"
                  v-model="documentExceptionPrefs.sendEmail"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center"></td>
              <td class="px-3 py-4 text-center"></td>
            </tr>

            <!-- External Policy Ready for Review -->
            <tr class="hover:bg-gray-50/50">
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <FileSearchIcon
                    :size="18"
                    class="shrink-0"
                    :class="externalPolicyReadyForReviewPrefs.enabled ? 'text-emerald-500' : 'text-gray-400'" />
                  <div class="text-sm font-medium text-gray-900">External policy ready for review</div>
                </div>
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="externalPolicyReadyForReviewEnabled"
                  :disabled="!generalPrefs.notificationsEnabled"
                  @change="handlePreferenceUpdate"
                  v-model="externalPolicyReadyForReviewPrefs.enabled"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="externalPolicyReadyForReviewSendEmail"
                  :disabled="!generalPrefs.notificationsEnabled || !externalPolicyReadyForReviewPrefs.enabled || !generalPrefs.sendEmail"
                  @change="handlePreferenceUpdate"
                  v-model="externalPolicyReadyForReviewPrefs.sendEmail"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center"></td>
              <td class="px-3 py-4 text-center"></td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Client user: Analytics Resource -->
      <section
        v-if="isClientUser"
        class="overflow-hidden rounded-xl border border-solid border-gray-200 drop-shadow-xs">
        <table class="w-full">
          <thead>
            <tr class="border-b border-solid border-gray-200 bg-gray-50 text-xs tracking-wide text-gray-500 uppercase">
              <th class="px-5 py-3 text-left font-semibold">Notification</th>
              <th class="w-16 px-3 py-3 text-center font-semibold">In-App</th>
              <th class="w-16 px-3 py-3 text-center font-semibold">Email</th>
            </tr>
          </thead>
          <tbody>
            <tr class="hover:bg-gray-50/50">
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <BarChart3Icon
                    :size="18"
                    class="shrink-0"
                    :class="newAnalyticsResourcePrefs.enabled ? 'text-emerald-500' : 'text-gray-400'" />
                  <div class="text-sm font-medium text-gray-900">New analytics resource available</div>
                </div>
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newAnalyticsResourceEnabled"
                  :disabled="!generalPrefs.notificationsEnabled"
                  @change="handlePreferenceUpdate"
                  v-model="newAnalyticsResourcePrefs.enabled"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
              <td class="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  id="newAnalyticsResourceSendEmail"
                  :disabled="!generalPrefs.notificationsEnabled || !newAnalyticsResourcePrefs.enabled || !generalPrefs.sendEmail"
                  @change="handlePreferenceUpdate"
                  v-model="newAnalyticsResourcePrefs.sendEmail"
                  class="checkbox rounded border border-solid border-gray-300 checkbox-sm checkbox-success" />
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Legend -->
      <div class="mx-auto mt-6 grid max-w-fit grid-cols-[auto_auto_1fr] items-center gap-x-2 gap-y-2 px-5 text-xs text-gray-500">
        <BellIcon
          :size="14"
          class="text-gray-400" />
        <span class="font-semibold text-gray-700 uppercase">In-app</span>
        <span>Show in the notification bell dropdown inside this app.</span>

        <MailIcon
          :size="14"
          class="text-gray-400" />
        <span class="font-semibold text-gray-700 uppercase">Email</span>
        <span>Include in the daily email summary sent to your inbox.</span>

        <UserIcon
          :size="14"
          fill="currentColor"
          class="text-gray-400" />
        <span class="font-semibold text-gray-700 uppercase">Role</span>
        <span>Trigger only for accounts where you hold a servicing role.</span>

        <StarIcon
          :size="14"
          fill="currentColor"
          class="text-gray-400" />
        <span class="font-semibold text-gray-700 uppercase">Fav</span>
        <span>Trigger only for accounts you've favorited.</span>
      </div>
    </main>
  </div>
</template>

<style scoped>
.save-badge-enter-active,
.save-badge-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.save-badge-enter-from,
.save-badge-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
